import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
// NOTE: `sonner` is a required dependency (see package.json). It powers the
// <Toaster /> mounted in App.js. Do not remove it — this is the only importer.
import { toast } from 'sonner';
import NLTWatermark from './NLTWatermark';
import { 
  Upload, X, Video, Image, Loader2, CheckCircle, AlertCircle,
  Play, Trash2, FileVideo, FileImage, Heart, Scissors, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Media Upload Component for Artists
 * Supports video (up to 500MB, 10 min) and image uploads
 */
const MediaUpload = ({ onUploadComplete, allowedTypes = ['video', 'image'] }) => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [previewError, setPreviewError] = useState(false);
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);

  const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
  const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB

  // Capture a poster frame from the selected video entirely in the browser.
  // This guarantees every video gets a thumbnail without depending on the
  // server-side ffmpeg transcode (which can fail/stall in production).
  // Returns a JPEG Blob, or null if the browser can't decode the format
  // (e.g. some HEVC .mov on desktop Chrome) — in which case the server falls back.
  const generateVideoThumbnail = (videoFile) => new Promise((resolve) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      const url = URL.createObjectURL(videoFile);
      video.src = url;
      let settled = false;
      const finish = (blob) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(url);
        resolve(blob);
      };
      video.onloadedmetadata = () => {
        const t = Math.min(1, (video.duration || 2) / 2);
        try { video.currentTime = t; } catch (e) { finish(null); }
      };
      video.onseeked = () => {
        try {
          const w = video.videoWidth, h = video.videoHeight;
          if (!w || !h) return finish(null);
          const maxDim = 640;
          const scale = Math.max(w, h) > maxDim ? maxDim / Math.max(w, h) : 1;
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => finish(blob), 'image/jpeg', 0.8);
        } catch (e) {
          finish(null);
        }
      };
      video.onerror = () => finish(null);
      setTimeout(() => finish(null), 8000); // safety valve
    } catch (e) {
      resolve(null);
    }
  });

  // Upload a thumbnail Blob to R2 via a presigned image URL; returns its r2_key.
  const uploadThumbnail = async (blob) => {
    try {
      const res = await fetch(
        `${API_URL}/api/radio/media/presigned-upload?filename=thumb.jpg&media_type=image&content_type=${encodeURIComponent('image/jpeg')}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!data.upload_url || !data.r2_key) return null;
      const putRes = await fetch(data.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob
      });
      return putRes.ok ? data.r2_key : null;
    } catch (e) {
      return null;
    }
  };

  const finalizeUpload = async (media) => {
    // Fire immediately — the gallery displays the item and auto-polls the
    // "Optimizing…" state until the server finishes transcoding, so we don't
    // block the upload form waiting.
    onUploadComplete && onUploadComplete(media);
    resetForm();
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setError(null);
    
    // Determine media type
    const isVideo = selectedFile.type.startsWith('video');
    const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
    
    // Validate size
    if (selectedFile.size > maxSize) {
      const maxMB = maxSize / (1024 * 1024);
      setError(`File too large. Maximum ${maxMB}MB for ${isVideo ? 'videos' : 'images'}`);
      return;
    }
    
    // Validate type
    const allowedExtensions = isVideo 
      ? ['.mp4', '.mov', '.avi', '.mkv', '.webm']
      : ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const ext = '.' + selectedFile.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(ext)) {
      setError(`Invalid file type. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }
    
    setFile(selectedFile);
    setTitle(selectedFile.name.replace(/\.[^/.]+$/, "")); // Default title from filename
    setPreviewError(false);

    // Use an object URL for instant preview (avoids huge base64 data URLs).
    // Note: some formats (e.g. iPhone HEVC .mov) can't be previewed by the
    // browser — we show a graceful fallback but the file still uploads fine.
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const objectUrl = URL.createObjectURL(selectedFile);
    objectUrlRef.current = objectUrl;
    setPreview(objectUrl);
  };

  const handleUpload = async () => {
    if (!file || !token) return;
    
    setUploading(true);
    setProgress(0);
    setError(null);
    
    const isVideo = file.type.startsWith('video');
    const mediaType = isVideo ? 'video' : 'image';

    try {
      // For videos, generate a poster thumbnail in the browser and upload it to
      // R2 first, so the video is immediately usable (has a thumbnail + plays)
      // without waiting on the server transcode.
      let thumbnailKey = null;
      if (isVideo) {
        const thumbBlob = await generateVideoThumbnail(file);
        if (thumbBlob) {
          thumbnailKey = await uploadThumbnail(thumbBlob);
        }
      }

      // For large files, use presigned URL
      if (file.size > 50 * 1024 * 1024) {
        // Get presigned URL
        const presignRes = await fetch(
          `${API_URL}/api/radio/media/presigned-upload?filename=${encodeURIComponent(file.name)}&media_type=${mediaType}&content_type=${encodeURIComponent(file.type)}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const presignData = await presignRes.json();
        
        if (!presignData.upload_url) {
          throw new Error('Failed to get upload URL');
        }
        
        // Upload directly to R2
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
        
        await new Promise((resolve, reject) => {
          xhr.open('PUT', presignData.upload_url);
          xhr.setRequestHeader('Content-Type', file.type);
          
          xhr.onload = () => {
            if (xhr.status === 200) resolve();
            else reject(new Error(`Upload failed: ${xhr.status}`));
          };
          xhr.onerror = () => reject(new Error('Upload failed'));
          
          xhr.send(file);
        });
        
        // Confirm upload
        const confirmData = new FormData();
        confirmData.append('media_id', presignData.media_id);
        confirmData.append('r2_key', presignData.r2_key);
        confirmData.append('media_type', mediaType);
        if (title) confirmData.append('title', title);
        if (description) confirmData.append('description', description);
        if (thumbnailKey) confirmData.append('thumbnail_key', thumbnailKey);
        
        const confirmRes = await fetch(`${API_URL}/api/radio/media/confirm-upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: confirmData
        });
        
        const result = await confirmRes.json();
        
        if (result.success) {
          await finalizeUpload(result.media);
        } else {
          throw new Error(result.detail || 'Upload confirmation failed');
        }
      } else {
        // Direct upload for smaller files
        const formData = new FormData();
        formData.append('file', file);
        formData.append('media_type', mediaType);
        if (title) formData.append('title', title);
        if (description) formData.append('description', description);
        if (thumbnailKey) formData.append('thumbnail_key', thumbnailKey);
        
        const res = await fetch(`${API_URL}/api/radio/media/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        const result = await res.json();
        
        if (result.success) {
          await finalizeUpload(result.media);
        } else {
          throw new Error(result.detail || 'Upload failed');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setFile(null);
    setPreview(null);
    setPreviewError(false);
    setTitle('');
    setDescription('');
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-gray-800 rounded-xl p-4">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Upload className="w-5 h-5 text-purple-400" />
        Upload Media
      </h3>

      {!file ? (
        <div 
          className="relative border-2 border-dashed border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-500/5 transition"
          data-testid="media-upload-dropzone"
        >
          {/* Invisible file input covering the entire dropzone */}
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedTypes.includes('video') && allowedTypes.includes('image') 
              ? 'video/*,image/*' 
              : allowedTypes.includes('video') ? 'video/*' : 'image/*'
            }
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            data-testid="media-upload-input"
          />
          <div className="flex justify-center gap-4 mb-4">
            {allowedTypes.includes('video') && (
              <div className="flex flex-col items-center">
                <Video className="w-10 h-10 text-blue-400" />
                <span className="text-xs text-gray-400 mt-1">Video</span>
              </div>
            )}
            {allowedTypes.includes('image') && (
              <div className="flex flex-col items-center">
                <Image className="w-10 h-10 text-green-400" />
                <span className="text-xs text-gray-400 mt-1">Image</span>
              </div>
            )}
          </div>
          <p className="text-gray-400 text-sm">
            Click to select or drag & drop
          </p>
          <p className="text-gray-500 text-xs mt-2">
            Videos: MP4, MOV, AVI (up to 500MB, 10 min) • Images: JPG, PNG, GIF (up to 20MB)
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Preview */}
          <div className="relative rounded-xl overflow-hidden bg-black">
            {file.type.startsWith('video') ? (
              previewError ? (
                <div className="w-full h-48 flex flex-col items-center justify-center text-center px-4 bg-gray-900">
                  <FileVideo className="w-10 h-10 text-blue-400 mb-2" />
                  <p className="text-sm text-gray-200 font-medium truncate max-w-full">{file.name}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Preview isn't supported for this format, but your video will upload and play fine after optimizing.
                  </p>
                </div>
              ) : (
                <video 
                  src={preview} 
                  className="w-full max-h-64 object-contain"
                  controls
                  playsInline
                  onError={() => setPreviewError(true)}
                />
              )
            ) : (
              <img 
                src={preview} 
                alt="Preview" 
                className="w-full max-h-64 object-contain"
              />
            )}
            <button 
              onClick={resetForm}
              className="absolute top-2 right-2 p-2 bg-black/60 rounded-full hover:bg-black/80"
            >
              <X className="w-4 h-4" />
            </button>
            
            {/* File Info Badge */}
            <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full">
              {file.type.startsWith('video') ? (
                <FileVideo className="w-4 h-4 text-blue-400" />
              ) : (
                <FileImage className="w-4 h-4 text-green-400" />
              )}
              <span className="text-xs">{formatFileSize(file.size)}</span>
            </div>
          </div>

          {/* Title & Description */}
          <div className="space-y-3">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              className="w-full bg-gray-700 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              maxLength={100}
              data-testid="media-title-input"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
              className="w-full bg-gray-700 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              rows={3}
              maxLength={500}
              data-testid="media-description-input"
            />
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Uploading...</span>
                <span className="text-purple-400">{progress}%</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            className="w-full py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-medium transition flex items-center justify-center gap-2"
            data-testid="media-upload-submit-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Upload {file.type.startsWith('video') ? 'Video' : 'Image'}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Media Gallery Component
 * Display uploaded videos and images
 */
export const MediaGallery = ({ artistId, canDelete = false, refreshKey = 0 }) => {
  const { token: authToken } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, video, image
  const [selectedMedia, setSelectedMedia] = useState(null);
  
  // Fallback to localStorage if context token is not available
  const token = authToken || localStorage.getItem('token');

  const [sharingClip, setSharingClip] = useState(false);
  const [sharedClipIds, setSharedClipIds] = useState([]);
  const [postingStory, setPostingStory] = useState(false);
  const [storyPostedIds, setStoryPostedIds] = useState([]);
  const [trimOpen, setTrimOpen] = useState(false);
  const [clipStart, setClipStart] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const modalVideoRef = useRef(null);

  const CLIP_LEN = 60;
  const fmt = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  const handleShareToClip = async (item) => {
    if (!token || sharingClip) return;
    setSharingClip(true);
    try {
      const startParam = trimOpen ? `?start=${Math.floor(clipStart)}` : '';
      const res = await fetch(`${API_URL}/api/radio/clips/from-media/${item.id}${startParam}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSharedClipIds(prev => [...prev, item.id]);
        setTrimOpen(false);
        toast.success('Shared to Clips! It will appear once optimized (60s teaser).');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Could not share to Clips.');
      }
    } catch (err) {
      toast.error('Could not share to Clips. Please try again.');
    } finally {
      setSharingClip(false);
    }
  };

  const handlePostToStory = async (item) => {
    if (!token || postingStory) return;
    setPostingStory(true);
    try {
      const res = await fetch(`${API_URL}/api/radio/stories/from-media/${item.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setStoryPostedIds(prev => [...prev, item.id]);
        toast.success('Posted to your Story! It stays live for 24 hours.');
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Could not post to your Story.');
      }
    } catch (err) {
      toast.error('Could not post to your Story. Please try again.');
    } finally {
      setPostingStory(false);
    }
  };

  const handleLike = async (e, item) => {
    e.stopPropagation();
    e.preventDefault();
    if (!token) return;
    const liked = item.user_liked;
    // Optimistic update
    setMedia(prev => prev.map(m => m.id === item.id
      ? { ...m, user_liked: !liked, like_count: (m.like_count || 0) + (liked ? -1 : 1) }
      : m));
    setSelectedMedia(sm => sm && sm.id === item.id
      ? { ...sm, user_liked: !liked, like_count: (sm.like_count || 0) + (liked ? -1 : 1) }
      : sm);
    try {
      await fetch(`${API_URL}/api/radio/media/${item.id}/like`, {
        method: liked ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      // Revert on failure
      setMedia(prev => prev.map(m => m.id === item.id
        ? { ...m, user_liked: liked, like_count: (m.like_count || 0) + (liked ? 1 : -1) }
        : m));
    }
  };

  const fetchMedia = useCallback(async () => {
    if (!artistId) return;
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('media_type', filter);

      const res = await fetch(`${API_URL}/api/radio/media/artist/${artistId}?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      const data = await res.json();
      setMedia(data.media || []);
    } catch (err) {
      console.error('Fetch media error:', err);
    } finally {
      setLoading(false);
    }
  }, [artistId, filter, token]);

  React.useEffect(() => {
    setLoading(true);
    fetchMedia();
  }, [fetchMedia, refreshKey]);

  // Auto-refresh while any video is still transcoding so the "Optimizing…"
  // overlay clears on its own once the server finishes (no manual reload).
  const hasProcessing = media.some(m => m.status === 'processing');
  React.useEffect(() => {
    if (!hasProcessing) return;
    const interval = setInterval(fetchMedia, 4000);
    // Stop polling after ~3 minutes as a safety valve
    const stop = setTimeout(() => clearInterval(interval), 180000);
    return () => { clearInterval(interval); clearTimeout(stop); };
  }, [hasProcessing, fetchMedia]);

  const handleDelete = async (mediaId) => {
    console.log('handleDelete called with mediaId:', mediaId);
    console.log('Token available:', !!token);
    
    if (!token) {
      console.error('No token available for delete');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this media?')) {
      return;
    }
    
    try {
      console.log('Sending DELETE request for:', mediaId);
      
      const res = await fetch(`${API_URL}/api/radio/media/${mediaId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Delete response status:', res.status);
      
      if (res.ok) {
        setMedia(prev => prev.filter(m => m.id !== mediaId));
        console.log('Media deleted successfully');
      } else {
        const data = await res.json().catch(() => ({}));
        console.error('Delete failed:', data);
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {['all', 'video', 'image'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              filter === f 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {f === 'all' ? 'All' : f === 'video' ? 'Videos' : 'Images'}
          </button>
        ))}
      </div>

      {/* Grid */}
      {media.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {media.map(item => (
            <div 
              key={item.id}
              className="relative aspect-square rounded-xl overflow-hidden bg-gray-800 cursor-pointer group"
              onClick={(e) => {
                // Only open modal if not clicking delete or like button
                if (!e.target.closest('[data-delete-btn]') && !e.target.closest('[data-like-btn]')) {
                  setSelectedMedia(item);
                }
              }}
            >
              {item.media_type === 'video' ? (
                <>
                  {item.thumbnail_url ? (
                    <img
                      src={item.thumbnail_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video 
                      src={item.playback_url || item.url} 
                      className="w-full h-full object-cover"
                      preload="metadata"
                      muted
                    />
                  )}
                  {item.status === 'processing' ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 pointer-events-none">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                      <span className="text-xs text-white mt-2">Optimizing…</span>
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                      <Play className="w-10 h-10 text-white" fill="currentColor" />
                    </div>
                  )}
                </>
              ) : (
                <img 
                  src={item.playback_url || item.url} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Title Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                <p className="text-sm font-medium truncate">{item.title}</p>
              </div>

              {/* Like Button */}
              {token && (
                <button
                  data-like-btn="true"
                  onClick={(e) => handleLike(e, item)}
                  className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 hover:bg-black/80 transition z-20"
                  data-testid={`like-media-${item.id}`}
                  type="button"
                >
                  <Heart className={`w-4 h-4 ${item.user_liked ? 'text-[#FF007F] fill-[#FF007F]' : 'text-white'}`} />
                  {item.like_count > 0 && (
                    <span className="text-xs text-white">{item.like_count}</span>
                  )}
                </button>
              )}
              
              {/* Delete Button - Always visible on mobile, hover on desktop */}
              {canDelete && (
                <button
                  data-delete-btn="true"
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    e.preventDefault(); 
                    console.log('Delete thumbnail clicked:', item.id);
                    handleDelete(item.id); 
                  }}
                  className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition z-20 shadow-lg cursor-pointer"
                  data-testid={`delete-media-${item.id}`}
                  type="button"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No media uploaded yet</p>
        </div>
      )}

      {/* Media Viewer Modal */}
      {selectedMedia && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => { setSelectedMedia(null); setTrimOpen(false); }}
        >
          {/* Close Button */}
          <button 
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition"
            onClick={() => setSelectedMedia(null)}
            data-testid="close-media-viewer"
          >
            <X className="w-8 h-8" />
          </button>
          
          {/* Delete Button in Modal */}
          {canDelete && (
            <button
              className="absolute top-4 left-4 p-3 bg-red-500 hover:bg-red-600 rounded-full transition flex items-center gap-2 z-[60]"
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                console.log('Modal delete clicked for:', selectedMedia.id);
                
                if (!token) {
                  console.error('No token for modal delete');
                  return;
                }
                
                if (!window.confirm('Delete this media?')) {
                  return;
                }
                
                try {
                  const res = await fetch(`${API_URL}/api/radio/media/${selectedMedia.id}`, {
                    method: 'DELETE',
                    headers: { 
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  
                  if (res.ok) {
                    setMedia(prev => prev.filter(m => m.id !== selectedMedia.id));
                    setSelectedMedia(null);
                    console.log('Deleted from modal');
                  } else {
                    console.error('Modal delete failed:', res.status);
                  }
                } catch (err) {
                  console.error('Modal delete error:', err);
                }
              }}
              data-testid="delete-media-modal"
              type="button"
            >
              <Trash2 className="w-5 h-5 text-white" />
              <span className="text-white text-sm font-medium hidden sm:inline">Delete</span>
            </button>
          )}
          
          <div className="max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="relative inline-block">
              {selectedMedia.media_type === 'video' ? (
                <video 
                  ref={modalVideoRef}
                  src={selectedMedia.playback_url || selectedMedia.url}
                  className="w-full max-w-full max-h-[80vh] object-contain rounded-xl bg-black"
                  controls
                  autoPlay
                  onLoadedMetadata={(e) => setVideoDuration(e.target.duration || 0)}
                />
              ) : (
                <img 
                  src={selectedMedia.playback_url || selectedMedia.url}
                  alt={selectedMedia.title}
                  className="max-w-full max-h-[80vh] rounded-xl"
                />
              )}
              <NLTWatermark size="md" />
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-semibold">{selectedMedia.title}</h3>
              {selectedMedia.description && (
                <p className="text-gray-400 mt-2">{selectedMedia.description}</p>
              )}
              {token && (
                <button
                  onClick={(e) => handleLike(e, selectedMedia)}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition"
                  data-testid={`like-media-modal-${selectedMedia.id}`}
                  type="button"
                >
                  <Heart className={`w-5 h-5 ${selectedMedia.user_liked ? 'text-[#FF007F] fill-[#FF007F]' : 'text-white'}`} />
                  <span className="text-sm font-medium">
                    {selectedMedia.user_liked ? 'Liked' : 'Like'}
                    {selectedMedia.like_count > 0 ? ` · ${selectedMedia.like_count}` : ''}
                  </span>
                </button>
              )}
              {canDelete && selectedMedia.status !== 'processing' && (
                <button
                  onClick={() => handlePostToStory(selectedMedia)}
                  disabled={postingStory || storyPostedIds.includes(selectedMedia.id)}
                  className="mt-4 sm:ml-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-60 transition"
                  data-testid={`post-to-story-${selectedMedia.id}`}
                  type="button"
                >
                  {postingStory ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5 text-[#FF007F]" />}
                  <span className="text-sm font-medium">
                    {storyPostedIds.includes(selectedMedia.id) ? 'Added to Story' : 'Add to Story'}
                  </span>
                </button>
              )}
              {canDelete && selectedMedia.media_type === 'video' && selectedMedia.status !== 'processing' && (
                !trimOpen ? (
                  <button
                    onClick={() => { setClipStart(0); setTrimOpen(true); }}
                    disabled={sharedClipIds.includes(selectedMedia.id)}
                    className="mt-4 sm:ml-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF007F] hover:bg-[#e00072] disabled:opacity-60 transition"
                    data-testid={`share-to-clip-${selectedMedia.id}`}
                    type="button"
                  >
                    <Scissors className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      {sharedClipIds.includes(selectedMedia.id) ? 'Shared to Clips' : 'Share to Clips'}
                    </span>
                  </button>
                ) : (
                  <div className="mt-5 mx-auto max-w-md bg-white/5 border border-white/10 rounded-2xl p-4 text-left" data-testid={`trim-panel-${selectedMedia.id}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Scissors className="w-4 h-4 text-[#FF007F]" />
                      <span className="text-sm font-semibold text-white">Pick your 60-second clip</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0, Math.floor(videoDuration - 1))}
                      step={1}
                      value={clipStart}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setClipStart(v);
                        if (modalVideoRef.current) {
                          try { modalVideoRef.current.currentTime = v; } catch (err) {}
                        }
                      }}
                      className="w-full accent-[#FF007F] cursor-pointer"
                      data-testid={`trim-start-slider-${selectedMedia.id}`}
                    />
                    <div className="flex items-center justify-between text-xs text-gray-300 mt-2">
                      <span>Start: <b className="text-white">{fmt(clipStart)}</b></span>
                      <span>Clip: {fmt(clipStart)} → {fmt(Math.min(videoDuration, clipStart + CLIP_LEN))}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Drag to preview the start point. Captures up to {CLIP_LEN}s from here.
                    </p>
                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={() => handleShareToClip(selectedMedia)}
                        disabled={sharingClip}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#FF007F] hover:bg-[#e00072] disabled:opacity-60 transition"
                        data-testid={`create-clip-${selectedMedia.id}`}
                        type="button"
                      >
                        {sharingClip ? <Loader2 className="w-5 h-5 animate-spin" /> : <Scissors className="w-5 h-5" />}
                        <span className="text-sm font-medium">Create Clip</span>
                      </button>
                      <button
                        onClick={() => setTrimOpen(false)}
                        className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition text-sm font-medium"
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
